import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { isFuture, isPast, isToday } from 'date-fns';
import { ContactOfflineService } from '@omni/services/contact/contact.service';
import { ActivityService } from '@omni/services/activity/activity.service';
import { AccountOfflineService } from '@omni/services/account/account.offline.service';
import { FeatureActionsMap } from '@omni/classes/authentication/user.class';
import { AppointmentActivity } from '@omni/classes/activity/appointment.activity.class';
import { StoreCheckActivity } from '@omni/classes/activity/store-check.activity.class';
import { Brand } from '@omni/classes/brand/brand.class';
import { RxSalesDataService } from '@omni/services/contact-spotlight/rxsalesdata.service';
import { SuggestionsService } from '@omni/services/contact-spotlight/suggestions.service';
import { Period } from '@omni/services/contact-spotlight/rxsalesdata.types';
import { AdoptionLadderJourneyService } from '@omni/services/adoption-ladder-journey/adoption-ladder-journey.service';
import { JourneySuggestionType } from '@omni/classes/customer-segment/adoption-ladder.class';
import { CallPlanOfflineService } from '@omni/services/call-plan/call-plan.offline.service';
import { AuthenticationService } from '@omni/services/authentication.service';
import { OpportunityManagementService } from '@omni/services/opportunity-management/opportunity-management.service';
import { AccountManagementOfflineService } from '@omni/services/account-management/account-management.service';
import { QuotesService } from '@omni/services/quote/quote-tool.service';
import { ProcedureContractService } from '@omni/data-services/procedure-contract/procedure-contract.service';
import { ScientificActivityDataService } from '@omni/data-services/scientific-activity/scientific-activity.data.service';
import { ConsentService } from '@omni/services/consent/consent.service';
import { EventsToolService, EVENT_TOOLS_SUMMARY_DOC_ID } from '@omni/services/events-tool/events-tool.service';
import { CustomerAssessService } from '@omni/services/customer-assess/customer-assess.service';
import { DiskService, OFFLINE_DB_LINKED_ENTITY_NAME } from '@omni/services/disk/disk.service';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { ActivityType } from '@omni/classes/activity/activity.class';
import { OrderActivity } from '@omni/classes/activity/order.activity.class';
import { SurgeryOrderActivity } from '@omni/classes/activity/surgery-order.activity.class';
import { EmailActivity } from '@omni/classes/activity/email.activity.class';
import { PhoneActivity } from '@omni/classes/activity/phone.activity.class';
import { SampleActivity } from '@omni/classes/activity/sample.activity.class';
import { CaseActivity } from '@omni/classes/case-intake/case-activity.class';
import { AssessmentAttributeType, Control } from '@omni/classes/customer-assessment/assessment-template.class';
import { AIDataSource, SummarySection } from './data-source.decorator';
import type { DataSourceContext } from './data-source-registry.service';

const PERIOD_LABEL: Record<Period, string> = {
  [Period.CW]: 'Current Week',
  [Period.C3W]: 'Last 3 Weeks',
  [Period.C4W]: 'Last 4 Weeks',
  [Period.C6W]: 'Last 6 Weeks',
  [Period.C8W]: 'Last 8 Weeks',
  [Period.C13W]: 'Last 13 Weeks',
  [Period.CM]: 'Current Month',
  [Period.C3M]: 'Last 3 Months',
  [Period.C6M]: 'Last 6 Months',
  [Period.QTD]: 'Quarter to Date',
  [Period.YTD]: 'Year to Date',
};

/** Short-to-long window order for emitting per-product Rx metrics. */
const PERIOD_SEQUENCE: Period[] = [
  Period.CW, Period.C3W, Period.C4W, Period.C6W, Period.C8W, Period.C13W,
  Period.CM, Period.C3M, Period.C6M, Period.QTD, Period.YTD,
];

const JOURNEY_TYPE_LABEL: Record<number, string> = {
  [JourneySuggestionType.OBJECTIVE]: 'Objective',
  [JourneySuggestionType.REACH]: 'Reach',
  [JourneySuggestionType.DEPTH]: 'Depth',
  [JourneySuggestionType.INTERACTION]: 'Interaction',
  [JourneySuggestionType.KEY_MESSAGE]: 'Key Message',
  [JourneySuggestionType.CONTENT]: 'Content',
};

/**
 * Personal-data denylist for assessment attributes (OMNI-59951 decision #2): attribute
 * labels matching these terms are NEVER emitted into the AI context — assessment
 * templates carry personal fields (e.g. "Marital Status" observed live on AddonQA).
 * Word-boundary matching so business labels like "Adoption Stage" or "Usage" are not
 * collateral of the `age` term. An org-specific allowlist can later override via review.
 */
const ASSESSMENT_PERSONAL_DATA_DENYLIST =
  /\b(marital|spouse|family|religion|birth|age|gender|personal\s+(phone|email|address))\b/i;

function getContactNameFromRow(row: any): string {
  return row?.contact?.fullName ?? row?.contact?.fullname ?? row?.contact?.ID ?? 'Unknown';
}

/** Epoch ms from the many date shapes the offline stores carry (ms-string, ISO string, Date, number). */
function toEpoch(value: unknown): number {
  if (value === null || value === undefined || value === '') return 0;
  const normalized = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value;
  const time = new Date(normalized as string | number | Date).getTime();
  return isNaN(time) ? 0 : time;
}

function fmtDate(value: unknown): string | null {
  const time = toEpoch(value);
  return time ? new Date(time).toLocaleDateString() : null;
}

function truncateText(text: string, max: number): string {
  const trimmed = text.trim();
  return trimmed.length > max ? `${trimmed.slice(0, max)}…` : trimmed;
}

/** Human-readable answer for one assessment response row, resolved through its template control. */
function assessmentResponseValue(resp: any, control: Control): string | null {
  switch (control.Type) {
    case AssessmentAttributeType.TEXT:
      return resp.indskr_text || null;
    case AssessmentAttributeType.MEMO:
      return resp.indskr_memo || null;
    case AssessmentAttributeType.NUMERIC:
    case AssessmentAttributeType.RANGE:
      return resp.indskr_numeric !== undefined && resp.indskr_numeric !== null && resp.indskr_numeric !== ''
        ? String(resp.indskr_numeric)
        : null;
    case AssessmentAttributeType.TIMESTAMP:
      return fmtDate(resp.indskr_timestamp);
    case AssessmentAttributeType.FLAG:
      return Object.prototype.hasOwnProperty.call(resp, 'indskr_flag') ? (resp.indskr_flag ? 'Yes' : 'No') : null;
    case AssessmentAttributeType.CHOICE: {
      const selectedIds: string[] =
        typeof resp.indskr_choicevalues === 'string' && resp.indskr_choicevalues
          ? resp.indskr_choicevalues.split(',')
          : [];
      const labels = (control.Choice?.Options ?? [])
        .filter(o => o.RatingProperties?.[0]?.GUID && selectedIds.includes(o.RatingProperties[0].GUID))
        .map(o => o.Label)
        .filter(Boolean);
      if (labels.length > 0) return labels.join(', ');
      return resp.indskr_choicelabel || null;
    }
    default:
      return null;
  }
}

function callPlanChannel(channel: string, actual: number, goal: number): { channel: string; actual: number; goal: number } | null {
  return goal > 0 ? { channel, actual, goal } : null;
}

/**
 * One product rendered for grounding — its name plus its key-message names. `deliveredOnly` keeps
 * only the key messages the rep actually delivered (`isSelected`), mirroring
 * `AppointmentActivity.productsDTO`; the default emits the product's full message list.
 */
function productToData(p: Brand, deliveredOnly = false): { name: string; keyMessages: string[] } {
  const messages = deliveredOnly ? (p.keyMessages ?? []).filter(k => k.isSelected) : (p.keyMessages ?? []);
  return { name: p.name, keyMessages: messages.map(k => k.name).filter(Boolean) as string[] };
}

/**
 * The products the rep actually detailed in a meeting: the `isSelected` subset, each carrying only
 * its delivered (`isSelected`) key messages — mirroring `AppointmentActivity.productsDTO`, which
 * filters both levels. `products` otherwise holds the full detailing catalogue, so an unfiltered map
 * would ground on every product — and every key message — in the territory.
 */
function meetingProducts(m: AppointmentActivity): { name: string; keyMessages: string[] }[] {
  return (m.products ?? []).filter(p => p.isSelected).map(p => productToData(p, true));
}

/** Structured form of a meeting — subject, date, status, products/key-messages, presentations, notes. */
function meetingToData(m: AppointmentActivity): Record<string, unknown> {
  const products = meetingProducts(m);
  const presentations = (m.presentations ?? [])
    .map(p => ('name' in p ? p.name : null) ?? ('title' in p ? (p as any).title : null))
    .filter(Boolean) as string[];
  // The meeting's notes live in the meetingNotes array (indskr annotations), not the flat `notes`
  // string; join the non-deleted note bodies so they reach the summary.
  const noteText = [...new Set((m.meetingNotes ?? [])
    .filter(n => n && !n.isDeleted && n.noteText)
    .map(n => n.noteText.trim())
    .filter(Boolean))]
    .join(' • ') || (m.notes ?? '');
  return {
    subject: m.subject || 'Meeting',
    date: m.scheduledStart ? new Date(m.scheduledStart).toLocaleDateString() : null,
    status: m.statusString || null,
    ...(products.length > 0 ? { products } : {}),
    ...(presentations.length > 0 ? { presentations } : {}),
    ...(noteText ? { notes: truncateText(noteText, 200) } : {}),
  };
}

/** Store checks are appointment-entity activities with their own category/notes rather
 *  than products or presentations, so they render with those fields instead. */
function storeCheckToData(s: StoreCheckActivity): Record<string, unknown> {
  return {
    activity: 'Store Check',
    subject: s.subject || 'Store Check',
    date: s.scheduledStart ? new Date(s.scheduledStart).toLocaleDateString() : null,
    status: s.statusString || null,
    ...(s.categoryName ? { category: s.categoryName } : {}),
    ...(s.notes ? { notes: truncateText(s.notes, 200) } : {}),
  };
}


/**
 * Per-section data sources for record/spotlight summaries. Each `@AIDataSource`
 * method produces one section's content; ordering and assembly into the section
 * list are owned by `AiDataSourceRegistry`, which calls each method as `method(ctx)`
 * so each source is independently configurable.
 */
@Injectable({ providedIn: 'root' })
export class RecordDataSourcesService {
  constructor(
    private contactService: ContactOfflineService,
    private activityService: ActivityService,
    private accountService: AccountOfflineService,
    private authService: AuthenticationService,
    private rxSalesDataService: RxSalesDataService,
    private suggestionsService: SuggestionsService,
    private adoptionLadderService: AdoptionLadderJourneyService,
    private callPlanService: CallPlanOfflineService,
    private opportunityService: OpportunityManagementService,
    private accountManagementService: AccountManagementOfflineService,
    private quotesService: QuotesService,
    private procedureContractService: ProcedureContractService,
    private scientificActivityDataService: ScientificActivityDataService,
    private consentService: ConsentService,
    private eventsToolService: EventsToolService,
    private customerAssessService: CustomerAssessService,
    private disk: DiskService,
  ) {}

  // ---------------------------------------------------------------------------
  // Base (always-on) sources
  // ---------------------------------------------------------------------------

  @AIDataSource('rep_profile', { label: 'User (Rep) Profile' })
  async repProfile(_ctx: DataSourceContext): Promise<SummarySection | null> {
    const user = this.authService.user;
    if (!user) return null;

    return {
      title: 'User (Rep) Profile',
      data: {
        id: user.systemUserID,
        name: [user.academicTitle, user.displayName].filter(Boolean).join(' '),
        title: user.jobTitle || null,
        position: user.positionName || null,
        businessUnit: user.businessUnitName || null,
      },
    };
  }

  @AIDataSource('contact_identity', { label: 'Contact (HCP) Profile' })
  async contactIdentity(ctx: DataSourceContext): Promise<SummarySection | null> {
    const contactId = ctx.recordId;
    if (!contactId) return null;

    const contact = this.contactService.getContactByID(contactId);
    if (!contact) return null;

    const flags: string[] = [];
    if (contact.isKOL === 'Yes') flags.push('KOL');
    if (contact.isSpeaker === 'Yes') flags.push('Speaker');

    return {
      title: 'Contact (HCP) Profile',
      data: {
        contactId,
        name: [contact.title, contact.fullName].filter(Boolean).join(' '),
        designation: contact.professionalDesignation?.designation || null,
        specialty: contact.combinedSpecialty || contact.primarySpecialtyName || null,
        flags,
        primaryAccount: contact.getPrimaryAccountRelationship || null,
        location: contact.getPrimaryCitySate || null,
        phone: contact.telephone || null,
        email: contact.getPrimaryEmail || null,
      },
    };
  }

  @AIDataSource('account_identity', { label: 'Account (HCO) Profile' })
  async accountIdentity(ctx: DataSourceContext): Promise<SummarySection | null> {
    const accountId = ctx.recordId;
    if (!accountId) return null;

    const account = this.accountService.getAccountById(accountId);
    if (!account) return null;

    // The account's address lives in the offline customer-address store, not on the account row —
    // same source the accounts table reads. City/country on the account row are the fallback.
    const primaryAddress = await this.accountService.getPrimaryAddressOfAccount(accountId).catch(() => null);
    const ext = account.extendedDetails;
    return {
      title: 'Account (HCO) Profile',
      data: {
        accountId,
        name: account.accountName,
        type: ext?.accountType || null,
        hospitalType: ext?.hospitalType || null,
        location: primaryAddress?.compositeAdd || [account.city, account.country].filter(Boolean).join(', ') || null,
      },
    };
  }

  // ---------------------------------------------------------------------------
  // Contact sources
  // ---------------------------------------------------------------------------

  @AIDataSource('contact_meetings', { label: 'Recent Meetings' })
  async contactMeetings(ctx: DataSourceContext): Promise<SummarySection | null> {
    const contactId = ctx.recordId;
    if (!contactId) return null;

    // Recent meetings from local offline cache — non-cancelled, non-deleted, and either already
    // past or scheduled for today, so a meeting created for later today shows straight away while
    // future-dated and cancelled/deleted meetings stay out.
    const meetings = (this.activityService.activities as AppointmentActivity[])
      .filter(a => a instanceof AppointmentActivity
        && !a.deleted
        && a.statusString !== 'Canceled'
        && (isPast(new Date(a.scheduledStart)) || isToday(new Date(a.scheduledStart)))
        && a.contacts?.some(c => c.ID === contactId))
      .sort((a, b) => +new Date(b.scheduledStart) - +new Date(a.scheduledStart))
      .slice(0, 10);

    if (meetings.length === 0) return null;
    return { title: 'Recent Meetings', data: { meetings: meetings.map(meetingToData) } };
  }

  @AIDataSource('contact_rx_sales', { label: 'Rx Sales' })
  async contactRxSales(ctx: DataSourceContext): Promise<SummarySection | null> {
    const contactId = ctx.recordId;
    if (!contactId) return null;

    const allRxData = await firstValueFrom(this.rxSalesDataService.rxData$).catch(() => null);
    if (!allRxData) return null;

    const contactRx = allRxData.filter(item => item.contact.ID === contactId);
    if (contactRx.length === 0) return null;

    // Orgs load either week-based (CW…C13W) or month-based (CM…YTD) windows, so include every
    // period that carries a metric. Rows group by product, periods emitted short-to-long.
    // Formatting is the serializer/agent's job, not ours.
    // `change` on every metric is a percentage vs the prior period (the territory tile shows it
    // as e.g. "900%"), so it is emitted as TRxChangePercent — never a raw count.
    interface PeriodMetrics { period: string; TRx?: number; TRxChangePercent?: number; NRx?: number; Refills?: number }
    const byProduct = new Map<string, PeriodMetrics[]>();

    const ordered = [...contactRx].sort((a, b) => PERIOD_SEQUENCE.indexOf(a.period) - PERIOD_SEQUENCE.indexOf(b.period));
    for (const item of ordered) {
      const productName = item.product.name ?? item.product.ID;
      const metrics: PeriodMetrics = { period: PERIOD_LABEL[item.period] ?? `Period ${item.period}` };
      let hasMetric = false;

      if ('TRx' in item && item.TRx) {
        metrics.TRx = item.TRx.value;
        if (item.TRx.change !== 0) metrics.TRxChangePercent = item.TRx.change;
        hasMetric = true;
      }
      if ('NRx' in item && item.NRx) {
        metrics.NRx = item.NRx.value;
        hasMetric = true;
      }
      if ('Refills' in item && item.Refills) {
        metrics.Refills = item.Refills.value;
        hasMetric = true;
      }

      if (!hasMetric) continue;

      const existing = byProduct.get(productName) ?? [];
      existing.push(metrics);
      byProduct.set(productName, existing);
    }

    if (byProduct.size === 0) return null;

    const products = [...byProduct.entries()].map(([product, periods]) => ({ product, periods }));
    return { title: 'Rx Sales', data: products };
  }

  @AIDataSource('contact_adoption_ladder', { label: 'Adoption Ladder' })
  async contactAdoptionLadder(ctx: DataSourceContext): Promise<SummarySection | null> {
    const contactId = ctx.recordId;
    if (!contactId) return null;

    // The summary works offline, so read the contact's product segmentations from the synced
    // offline contact record; the adoption-ladder fields live on each segmentation row.
    const contact = this.contactService.getContactByID(contactId);
    const segmentationData: any[] = contact?.productSegmentations ?? [];
    if (segmentationData.length === 0) return null;

    const products: { product: string; stage: string; rank: number | null }[] = [];
    for (const item of segmentationData) {
      const stage = item['adoptionladderName'] ?? null;
      if (!stage) continue;
      products.push({
        product: item['productName'] ?? item['segmentation'] ?? 'Unknown Product',
        stage,
        rank: item['adoptionladderRank'] ?? null,
      });
    }
    if (products.length === 0) return null;
    return { title: 'Adoption Ladder', data: { products } };
  }

  @AIDataSource('contact_journey', { label: 'Journey Suggestions' })
  async contactJourneySuggestions(ctx: DataSourceContext): Promise<SummarySection | null> {
    const contactId = ctx.recordId;
    if (!contactId) return null;

    let suggestions: any[];
    try {
      suggestions = await this.adoptionLadderService.getContactJourneySuggestionData(contactId);
    } catch {
      return null;
    }
    if (!suggestions || suggestions.length === 0) return null;

    const items: { type: string; name: string }[] = [];
    for (const s of suggestions.slice(0, 10)) {
      const type = JOURNEY_TYPE_LABEL[s.indskr_suggestiontype] ?? `Type ${s.indskr_suggestiontype}`;
      const name =
        s['keymessage.indskr_name'] ??
        s['iopresentation.indskr_title'] ??
        s['iopresentation.indskr_name'] ??
        s['iodocument.indskr_title'] ??
        s['ioresource.indskr_title'] ??
        s['masterdata.indskr_name'] ??
        null;
      if (name) items.push({ type, name });
    }
    if (items.length === 0) return null;
    return { title: 'Journey Suggestions', data: { suggestions: items } };
  }

  private async loadContactPlans(): Promise<any[] | null> {
    try {
      return await this.callPlanService.presentCallPlans('contact');
    } catch {
      return [];
    }
  }

  @AIDataSource('contact_call_plan', { label: 'Current Call Plan', featureAction: FeatureActionsMap.CALL_PLANS })
  async contactCallPlan(ctx: DataSourceContext): Promise<SummarySection | null> {
    const contactId = ctx.recordId;
    if (!contactId) return null;

    const plans = await this.loadContactPlans();
    const plan = plans?.find(p => p.contactId === contactId);
    if (!plan) return null;

    const channels = [
      callPlanChannel('Calls', plan.actualCalls, plan.totalGoalCalls),
      callPlanChannel('F2F Meetings', plan.actualF2FMeetings, plan.totalGoalF2FMeetings),
      callPlanChannel('Remote Meetings', plan.actualRemoteMeetings, plan.totalGoalRemoteMeetings),
      callPlanChannel('Emails', plan.actualEmails, plan.totalGoalEmails),
      callPlanChannel('Phone Calls', plan.actualPhoneCalls, plan.totalGoalPhoneCalls),
      callPlanChannel('Events', plan.actualEvents, plan.totalGoalEvents),
    ].filter(Boolean) as { channel: string; actual: number; goal: number }[];

    const productsInPlan = Array.isArray(plan.repPlans)
      ? [...new Set<string>(plan.repPlans.map((r: any) => r.productid_Formatted).filter(Boolean))]
      : [];

    return {
      title: 'Current Call Plan',
      data: {
        plan: plan.cycleplanname ?? null,
        endDate: fmtDate(plan.indskr_enddate),
        completionPct: plan.percentage ?? 0,
        completedGoals: plan.completedGoals ?? 0,
        definedGoals: plan.definedGoals ?? 0,
        channels,
        ...(productsInPlan.length > 0 ? { productsInPlan } : {}),
      },
    };
  }

  // ---------------------------------------------------------------------------
  // Account sources
  // ---------------------------------------------------------------------------

  @AIDataSource('account_affiliated_hcps', { label: 'Affiliated HCPs' })
  async accountAffiliatedHcps(ctx: DataSourceContext): Promise<SummarySection | null> {
    const accountId = ctx.recordId;
    if (!accountId) return null;

    const account = this.accountService.getAccountById(accountId);
    if (!account) return null;

    // The account's affiliations live in the synced account-side affiliation store — the same
    // source the account's Contact Affiliations tab reads — so every affiliated HCP appears,
    // including contacts outside the rep's own customer list. The explorer flag keeps the call
    // from overwriting the service's shared affiliation state.
    const A = 'indskr_accountcontactaffiliation';
    const fmt = (row: any, field: string): string | null =>
      row?.[`${A}.${field}_Formatted`] ?? row?.[`${A}.${field}@OData.Community.Display.V1.FormattedValue`] ?? null;
    let rows: any[] = [];
    try {
      rows = (await this.accountService.getAccountRelationshipById(accountId, true))?.linkedAccountContact ?? [];
    } catch {
      rows = [];
    }

    let hcps = rows
      .filter(r => fmt(r, 'indskr_contactid'))
      .slice(0, 10)
      .map(r => {
        const contact = this.contactService.getContactByID(r[`${A}.indskr_contactid`]);
        return {
          name: fmt(r, 'indskr_contactid'),
          specialty: contact?.primarySpecialtyName || null,
          role: fmt(r, 'indskr_contactrole') ?? fmt(r, 'omnione_role'),
          isPrimaryAccount: contact?.primaryAccount?.id === accountId,
        };
      });

    // Orgs without the affiliation link-entity synced: derive from the rep's own contacts.
    if (hcps.length === 0) {
      hcps = this.contactService.contacts
        .filter(c => c.isActive && (c.primaryAccount?.id === accountId || (c.accountRelationships ?? []).some(r => r?.accountId === accountId)))
        .slice(0, 10)
        .map(c => ({
          name: c.fullName,
          specialty: c.primarySpecialtyName || null,
          role: (c.accountRelationships ?? []).find(r => r?.accountId === accountId)?.contactRole || null,
          isPrimaryAccount: c.primaryAccount?.id === accountId,
        }));
    }
    if (hcps.length === 0) return null;
    return { title: 'Affiliated HCPs', data: { hcps } };
  }

  @AIDataSource('account_recent_activities', { label: 'Recent Activities' })
  async accountRecentActivities(ctx: DataSourceContext): Promise<SummarySection | null> {
    const accountId = ctx.recordId;
    if (!accountId) return null;

    const account = this.accountService.getAccountById(accountId);
    if (!account) return null;

    // Meetings and store checks are both appointment-entity activities on the account.
    // Appointment accounts are Account instances (.id); store-check accounts are raw
    // { indskr_accountid } — match the account id across both shapes.
    const activities = this.activityService.activities
      .filter((a): a is AppointmentActivity | StoreCheckActivity =>
        (a instanceof AppointmentActivity || a instanceof StoreCheckActivity)
        && ((a as any).accounts ?? []).some((acc: any) => (acc?.id ?? acc?.indskr_accountid) === accountId))
      .sort((a, b) => +new Date(b.scheduledStart) - +new Date(a.scheduledStart))
      .slice(0, 5);

    if (activities.length === 0) return null;
    return {
      title: 'Recent Activities',
      data: {
        activities: activities.map(a =>
          a instanceof StoreCheckActivity ? storeCheckToData(a) : meetingToData(a)),
      },
    };
  }

  // ---------------------------------------------------------------------------
  // Tab data sources (OMNI-59951): detail-page tab datasets exposed as
  // configurable summary sources. All reads are offline stores only.
  // ---------------------------------------------------------------------------

  @AIDataSource('contact_assessments', { label: 'Latest Assessment' })
  @AIDataSource('account_assessments', { label: 'Latest Assessment' })
  async recordAssessments(ctx: DataSourceContext): Promise<SummarySection | null> {
    const recordId = ctx.recordId;
    if (!recordId) return null;

    // Offline read: CUSTOMER_ASSESSMENT Pouch doc (keys are written lowercased).
    let assessment;
    try {
      assessment = await this.customerAssessService.getCustomerAssessmentFromDB(recordId.toLowerCase());
    } catch {
      return null;
    }
    if (!assessment || !Array.isArray(assessment.responses) || assessment.responses.length === 0) return null;

    // Attribute labels live on the template controls — responses only carry the control GUID.
    const controlsByGuid = new Map<string, Control>();
    const template = this.customerAssessService.assessmentTemplates
      .find(t => t.indskr_assessmenttemplateid === assessment.indskr_template);
    for (const section of template?.indskr_metadata?.Sections ?? []) {
      for (const control of section.Controls ?? []) {
        if (control.Attribute?.GUID) controlsByGuid.set(control.Attribute.GUID, control);
      }
    }
    if (controlsByGuid.size === 0) return null;

    // Latest answer per attribute, capped at 15, filtered through the personal-data denylist.
    const responses = [...assessment.responses].sort((a, b) => toEpoch(b?.modifiedon) - toEpoch(a?.modifiedon));
    const seenAttributes = new Set<string>();
    const attributes: { label: string; value: string }[] = [];
    for (const resp of responses) {
      if (attributes.length >= 15) break;
      const guid = resp?.indskr_attribute;
      if (!guid || seenAttributes.has(guid)) continue;
      seenAttributes.add(guid);
      const control = controlsByGuid.get(guid);
      const label = control?.DisplayName;
      if (!control || !label) continue;
      if (ASSESSMENT_PERSONAL_DATA_DENYLIST.test(label)) continue;
      const value = assessmentResponseValue(resp, control);
      if (!value) continue;
      attributes.push({ label, value });
    }
    if (attributes.length === 0) return null;

    return {
      title: 'Latest Assessment',
      data: {
        template: template?.indskr_name || null,
        date: fmtDate(assessment.indskr_assessmentdate),
        attributes,
      },
    };
  }

  @AIDataSource('contact_opportunities', { label: 'Opportunities' })
  @AIDataSource('account_opportunities', { label: 'Opportunities' })
  async recordOpportunities(ctx: DataSourceContext): Promise<SummarySection | null> {
    const recordId = ctx.recordId;
    if (!recordId) return null;

    // Same record filter as the omnichannel tab: account → customer lookup;
    // contact → primary target contact OR stakeholder.
    const matches = (this.opportunityService.opportunities ?? []).filter(o => {
      if (!o || o.isHardDeleted) return false;
      if (ctx.dataSourceId === 'account_opportunities') return o.accountID === recordId;
      return o.contactID === recordId || o.stakeholders?.some(s => s?.ID === recordId);
    });
    if (matches.length === 0) return null;

    const top = [...matches]
      .sort((a, b) => (Number(b.createdOn) || 0) - (Number(a.createdOn) || 0))
      .slice(0, 5)
      .map(o => ({
        name: o.opportunityName || 'Opportunity',
        stage: o.opportunityStageValue || null,
        status: o.state || null,
        estimatedClose: fmtDate(o.estimatedDate),
        estimatedRevenue: o.estimatedValue ? (o.estimatedValueFormatted || o.estimatedValue) : null,
      }));
    return { title: 'Opportunities', data: { opportunities: top } };
  }

  @AIDataSource('account_plans', { label: 'Account Plans' })
  async accountPlans(ctx: DataSourceContext): Promise<SummarySection | null> {
    const accountId = ctx.recordId;
    if (!accountId) return null;

    const plans = (this.accountManagementService.accountPlans ?? [])
      .filter(p => p && p.accountId === accountId && p.status === 'Open')
      .slice(0, 5);
    if (plans.length === 0) return null;

    const items = plans.map(p => {
      const start = fmtDate(p.startDate);
      const end = fmtDate(p.endDate);
      return {
        name: p.accountPlanName || 'Account Plan',
        period: start && end ? `${start} – ${end}` : p.accountPlanTimeFrame || null,
        objectives: (p.accountPlanObjectives ?? []).map(o => o?.name).filter(Boolean).slice(0, 5),
      };
    });
    return { title: 'Account Plans', data: { plans: items } };
  }

  @AIDataSource('contact_recent_interactions', { label: 'Recent Interactions' })
  async contactRecentInteractions(ctx: DataSourceContext): Promise<SummarySection | null> {
    const contactId = ctx.recordId;
    if (!contactId) return null;

    // Non-meeting channels only — meetings are already the 'meetings' source.
    // Record filters mirror contact-feed.ts.
    const interactions = this.activityService.activities
      .filter(a => {
        if (a instanceof EmailActivity) return a.emailActivityParties?.some(p => p?.indskr_contactid === contactId);
        // Phone calls carry their contact on raw_contactAttendees; `.contacts` is checked as a
        // secondary match, mirroring the contact feed.
        if (a instanceof PhoneActivity) return a.raw_contactAttendees?.some(c => c?.indskr_contactid === contactId) || a.contacts?.some(c => c?.ID === contactId);
        if (a instanceof SampleActivity) return a.contactID === contactId;
        if (a instanceof CaseActivity) return a._case_contact_id === contactId;
        // Surgery orders count as HCP interactions too; 548910005 = cancelled (feed filter).
        if (a instanceof SurgeryOrderActivity) return a.customers?.some(c => c?.ID === contactId) && a.status !== 548910005;
        return false;
      })
      .sort((a, b) => toEpoch(b.scheduledStart) - toEpoch(a.scheduledStart))
      .slice(0, 10);
    if (interactions.length === 0) return null;

    const items: Record<string, unknown>[] = [];
    for (const a of interactions) {
      const date = fmtDate(a.scheduledStart);
      if (a instanceof EmailActivity) {
        items.push({ channel: 'Email', subject: a.subject || 'No subject', date, via: a.channelName || null, status: a.emailStatusString || null });
      } else if (a instanceof PhoneActivity) {
        items.push({ channel: 'Phone Call', subject: a.subject || 'Phone Call', date, status: a.statusString || null });
      } else if (a instanceof SampleActivity) {
        // Sample activities in this app ARE allocation orders. The subject names the order — synced
        // ones carry the order number ("Order AO-…"), in-app ones the customer ("Allocation Order
        // with <name>") — so pass it through with the allocated products. A line missing its SKU
        // name still names its product through the lot names.
        const products = [...new Set<string>((a.samples ?? []).flatMap(s =>
          s?.indskr_skuname ? [s.indskr_skuname] : (s?.lots ?? []).map(l => l?.indskr_lotname).filter(Boolean) as string[]
        ))];
        items.push({ channel: 'Allocation Order', order: a.subject || null, date, products });
      } else if (a instanceof CaseActivity) {
        items.push({ channel: 'Customer Inquiry', subject: a.caseName || a.subject || 'Inquiry', date, status: a._case_status_value || null });
      } else if (a instanceof SurgeryOrderActivity) {
        const products = [...new Set<string>((a.products ?? []).map(p => p?.productName).filter(Boolean))];
        items.push({ channel: 'Surgery Order', subject: a.subject || 'Procedure Log', date, products });
      }
    }
    return { title: 'Recent Interactions', data: { interactions: items } };
  }

  @AIDataSource('account_quotes_orders', { label: 'Quotes, Orders & Contracts' })
  async accountQuotesOrders(ctx: DataSourceContext): Promise<SummarySection | null> {
    const accountId = ctx.recordId;
    if (!accountId) return null;

    const quotes = (this.quotesService.quotesList$.getValue() ?? [])
      .filter(q => q && q.accountId === accountId)
      .sort((a, b) => toEpoch(b.createdon) - toEpoch(a.createdon))
      .slice(0, 5)
      .map(q => ({ number: q.quoteNumber || q.name || 'Quote', status: q.qutoteStatus || null, total: q.totalAmount || null }));

    const orders = (this.activityService.activities
      .filter(a => a instanceof OrderActivity && a.type === ActivityType.Order && !a.isHardDeleted && a.accountId === accountId) as OrderActivity[])
      .sort((a, b) => toEpoch(b.createdDate) - toEpoch(a.createdDate))
      .slice(0, 5)
      .map(o => ({ number: o.orderNumber || o.name || 'Order', status: o.orderStatusString || null, total: o.formattedTotalAmount || (o.total ? String(o.total) : null) }));

    const contracts = (this.procedureContractService.procedureContracts ?? [])
      .filter(c => c && c.indskr_account_value === accountId)
      .slice(0, 5)
      .map(c => ({ name: c.indskr_name || 'Contract', status: c.statusString || null, endDate: fmtDate(c.indskr_enddate) }));

    if (quotes.length === 0 && orders.length === 0 && contracts.length === 0) return null;
    return {
      title: 'Quotes, Orders & Contracts',
      data: {
        ...(quotes.length > 0 ? { quotes } : {}),
        ...(orders.length > 0 ? { salesOrders: orders } : {}),
        ...(contracts.length > 0 ? { procedureContracts: contracts } : {}),
      },
    };
  }

  @AIDataSource('contact_scientific_plans', { label: 'Scientific Activity Plans' })
  async contactScientificPlans(ctx: DataSourceContext): Promise<SummarySection | null> {
    const contactId = ctx.recordId;
    if (!contactId) return null;

    // Resolves from the in-memory sapActivities store (offline-safe, no network).
    let plans;
    try {
      plans = await this.scientificActivityDataService.getScientificPlansByContactId(contactId);
    } catch {
      return null;
    }

    const now = Date.now();
    const currentOrFuture = (plans ?? [])
      .filter(p => p && toEpoch(p.endDate) >= now)
      .sort((a, b) => toEpoch(a.startDate) - toEpoch(b.startDate))
      .slice(0, 5);
    if (currentOrFuture.length === 0) return null;

    const items = currentOrFuture.map(p => ({
      phase: toEpoch(p.startDate) > now ? 'Upcoming' : 'Current',
      name: p.scientificActivityPlanName || 'Plan',
      startDate: fmtDate(p.startDate),
      endDate: fmtDate(p.endDate),
      status: p.status || null,
    }));
    return { title: 'Scientific Activity Plans', data: { plans: items } };
  }

  @AIDataSource('contact_notes', { label: 'Notes' })
  @AIDataSource('account_notes', { label: 'Notes' })
  async recordNotes(ctx: DataSourceContext): Promise<SummarySection | null> {
    const recordId = ctx.recordId;
    if (!recordId) return null;

    // Synced notes (annotation linked-entity doc) overlaid with offline-pending
    // notes (incl. deletions) — same assembly as contact-info / configured-account-info.
    const isAccount = ctx.dataSourceId === 'account_notes';
    let syncedRaw: any[] | null = null;
    if (isAccount) {
      const chunkKey = DB_KEY_PREFIXES.ACCOUNT_LINKED_ENTITY + OFFLINE_DB_LINKED_ENTITY_NAME.ACCOUNT_NOTES;
      syncedRaw = await this.disk.retrieveFromChunks(chunkKey).catch(() => null);
      if (!Array.isArray(syncedRaw) || syncedRaw.length === 0) {
        const doc = await this.disk.retrieve(chunkKey, true).catch(() => null);
        syncedRaw = doc?.raw;
      }
    } else {
      const doc = await this.disk.retrieve(OFFLINE_DB_LINKED_ENTITY_NAME.CONTACT_NOTES, true).catch(() => null);
      syncedRaw = doc?.raw;
    }

    const byId = new Map<string, { text: string; created: number }>();
    if (Array.isArray(syncedRaw)) {
      for (const item of syncedRaw) {
        if (!item || item['annotation.objectid'] !== recordId || !item['annotation.annotationid']) continue;
        const text = String(item['annotation.notetext'] ?? '').replace(/<[^>]*>/g, '').trim();
        if (!text) continue;
        byId.set(item['annotation.annotationid'], { text, created: toEpoch(item['annotation.createdon']) });
      }
    }

    const offlineKey = isAccount ? DB_KEY_PREFIXES.OFFLINE_ACCOUNT_NOTES : DB_KEY_PREFIXES.OFFLINE_CUSTOMER_NOTES;
    const offlineDoc = await this.disk.retrieve(offlineKey, true).catch(() => null);
    if (Array.isArray(offlineDoc?.raw)) {
      for (const item of offlineDoc.raw) {
        if (!item) continue;
        if (item.deleted) {
          byId.delete(item.annotationid);
          continue;
        }
        if ((isAccount ? item.accountid : item.contactid) !== recordId) continue;
        const text = String(item.notetext ?? '').replace(/<[^>]*>/g, '').trim();
        if (!text) continue;
        byId.set(item.annotationid, { text, created: toEpoch(item.createdon) });
      }
    }
    if (byId.size === 0) return null;

    const notes = [...byId.values()]
      .sort((a, b) => b.created - a.created)
      .slice(0, 5)
      .map(note => ({
        date: note.created ? new Date(note.created).toLocaleDateString() : null,
        text: truncateText(note.text, 200),
      }));
    return { title: 'Notes', data: { notes } };
  }

  @AIDataSource('account_journey', { label: 'Account Journey' })
  async accountJourney(ctx: DataSourceContext): Promise<SummarySection | null> {
    const accountId = ctx.recordId;
    if (!accountId) return null;

    // Account segmentation data has no synced offline store — the account page's Ladders tab
    // fetches it per account. Use the service's cached-or-fetch accessor, which returns the
    // in-memory copy when it already holds this account and comes back empty offline (the tab
    // shows nothing fresh offline either).
    let segmentations: any[] = [];
    try {
      segmentations = await this.adoptionLadderService.getAccountCustomerSegmentationData(accountId) ?? [];
    } catch {
      segmentations = [];
    }
    if (segmentations.length === 0) return null;

    // Account segmentation rows name the ladder stage as currentStageName/currentStageRank
    // (the Ladders tab's shape); keep the contact-style adoptionladder* names as a fallback.
    const products: { product: string; ladder: string | null; stage: string | null; rank: number | null }[] = [];
    for (const item of segmentations.slice(0, 10)) {
      const product = item['productName'] ?? item['productname'] ?? item['indskr_product_Formatted'] ?? 'Unknown Product';
      const stage = item['currentStageName'] ?? item['adoptionladderName'] ?? item['adoptionladdername'] ?? null;
      const rank = item['currentStageRank'] ?? item['adoptionladderRank'] ?? item['adoptionladderrank'] ?? null;
      const ladder = item['ladderTypeName'] ?? null;
      if (!stage) continue;
      products.push({ product, ladder, stage, rank });
    }
    if (products.length === 0) return null;
    return { title: 'Account Journey', data: { products } };
  }

  @AIDataSource('contact_consents', { label: 'Active Consents' })
  async contactConsents(ctx: DataSourceContext): Promise<SummarySection | null> {
    const contactId = ctx.recordId;
    if (!contactId) return null;

    try {
      await this.consentService.ensureActiveConsentsLoadedFromDisk();
    } catch {
      // keep whatever is already in memory
    }
    const contactConsent = (this.consentService.allActiveConsentSubject.getValue() ?? [])
      .find(cc => cc?.indskr_contactid === contactId);
    const terms = (contactConsent?.activeConsents ?? []).filter(Boolean);
    if (terms.length === 0) return null;

    const latest = [...terms]
      .sort((a, b) => (Number(b.createdon) || 0) - (Number(a.createdon) || 0))
      .slice(0, 10);

    // PRIVACY: emit channel + product + status + until-date ONLY — never the email
    // addresses, phone numbers, or other contact points captured with the consent.
    const now = Date.now();
    const consents = latest.map(term => {
      const until = Number(term.indskr_untildate) || null;
      return {
        channels: [...new Set<string>((term.channels ?? []).map(c => c?.indskr_consentType).filter(Boolean))],
        products: [...new Set<string>((term.products ?? []).map(p => p?.indskr_productname).filter(Boolean))],
        status: until && until < now ? 'Expired' : 'Active',
        until: until ? new Date(until).toLocaleDateString() : null,
      };
    });
    return { title: 'Active Consents', data: { consents } };
  }

  @AIDataSource('contact_events', { label: 'Events' })
  @AIDataSource('account_events', { label: 'Events' })
  async recordEvents(ctx: DataSourceContext): Promise<SummarySection | null> {
    const recordId = ctx.recordId;
    if (!recordId) return null;

    let items: { name: string; start: number; type: string | null; status: string | null }[];

    const inMemory = this.eventsToolService.eventsToolData;
    if (Array.isArray(inMemory) && inMemory.length > 0) {
      items = inMemory
        .filter(e => e && !e.isHardDeleted && (ctx.dataSourceId === 'account_events'
          ? e.accounts?.some(a => a && !a.isDeleted && a.id === recordId)
          : e.participants?.some(p => p && !p.isDeleted && p.id === recordId)))
        .map(e => ({
          name: e.name || 'Event',
          start: toEpoch(e.startDate),
          type: e.eventTypeFormatted || e.eventType || null,
          status: e.formattedStatus || null,
        }));
    } else {
      // Disk fallback: compact projection persisted by EventsToolService (write-through
      // cache). Defensive — the doc may not exist on installs that never ran initData.
      const doc = await this.disk.retrieve(EVENT_TOOLS_SUMMARY_DOC_ID, true).catch(() => null);
      const raw: any[] = Array.isArray(doc?.raw) ? doc.raw : [];
      items = raw
        .filter(e => e && (ctx.dataSourceId === 'account_events'
          ? Array.isArray(e.accountIds) && e.accountIds.includes(recordId)
          : Array.isArray(e.contactIds) && e.contactIds.includes(recordId)))
        .map(e => ({
          name: e.name || 'Event',
          start: toEpoch(e.startDate),
          type: e.eventType || null,
          status: e.status || null,
        }));
    }
    if (items.length === 0) return null;

    const now = Date.now();
    const toData = (e: { name: string; start: number; type: string | null; status: string | null }) => ({
      name: e.name,
      date: e.start ? new Date(e.start).toLocaleDateString() : null,
      type: e.type,
      status: e.status,
    });
    const upcoming = items.filter(e => e.start >= now).sort((a, b) => a.start - b.start).slice(0, 5).map(toData);
    const recent = items.filter(e => e.start < now).sort((a, b) => b.start - a.start).slice(0, 5).map(toData);

    if (upcoming.length === 0 && recent.length === 0) return null;
    return {
      title: 'Events',
      data: {
        ...(upcoming.length > 0 ? { upcoming } : {}),
        ...(recent.length > 0 ? { recent } : {}),
      },
    };
  }

  // ---------------------------------------------------------------------------
  // Meeting sources (recordType 'appointment' — the transcript-analysis scope)
  // ---------------------------------------------------------------------------

  /**
   * The meeting a meeting-scoped source grounds on: the open (hydrated) meeting when it matches the
   * record id, else the matching activity from the offline cache. Resolved through here so the
   * sources work whether or not the record id rode on the request.
   */
  private meetingInScope(recordId?: string): AppointmentActivity | null {
    const selected = this.activityService.selectedActivity;
    if (selected instanceof AppointmentActivity && (!recordId || selected.ID === recordId)) return selected;
    const found = this.activityService.activities.find(
      (a): a is AppointmentActivity => a instanceof AppointmentActivity && a.ID === recordId,
    );
    return found ?? null;
  }

  @AIDataSource('meeting_details', { label: 'Meeting Details' })
  async meetingDetails(ctx: DataSourceContext): Promise<SummarySection | null> {
    const m = this.meetingInScope(ctx.recordId);
    if (!m) return null;
    return {
      title: 'Meeting Details',
      data: {
        subject: m.subject || null,
        channel: m.isRemoteDetailing ? 'Remote' : 'In-person',
        date: m.scheduledStart ? new Date(m.scheduledStart).toLocaleString() : null,
        durationMinutes: m.actualdurationminutes || null,
        location: m.location || null,
        status: m.statusString || null,
        ...(m.notes ? { notes: truncateText(m.notes, 200) } : {}),
      },
    };
  }

  @AIDataSource('meeting_therapeutic_areas', { label: 'Therapeutic Areas' })
  async meetingTherapeuticAreas(ctx: DataSourceContext): Promise<SummarySection | null> {
    const m = this.meetingInScope(ctx.recordId);
    if (!m) return null;
    const therapeuticAreas = (m.activityTherapeuticAreas ?? []).map(ta => ta.name).filter(Boolean);
    return therapeuticAreas.length ? { title: 'Therapeutic Areas', data: { therapeuticAreas } } : null;
  }

  @AIDataSource('meeting_products', { label: 'Products Detailed' })
  async meetingDetailedProducts(ctx: DataSourceContext): Promise<SummarySection | null> {
    const m = this.meetingInScope(ctx.recordId);
    if (!m) return null;
    const products = meetingProducts(m);
    return products.length ? { title: 'Products Detailed', data: { products } } : null;
  }

  @AIDataSource('meeting_product_catalog', { label: 'Product Catalog' })
  async meetingProductCatalog(ctx: DataSourceContext): Promise<SummarySection | null> {
    const m = this.meetingInScope(ctx.recordId);
    if (!m) return null;
    // The full detailable catalogue (all products + key messages) so the agent can recognise any
    // product raised in the transcript, not only the ones the rep pre-selected.
    const products = (m.products ?? []).map(p => productToData(p));
    return products.length ? { title: 'Product Catalog (all detailable products)', data: { products } } : null;
  }

  @AIDataSource('meeting_content', { label: 'Content Presented' })
  async meetingContent(ctx: DataSourceContext): Promise<SummarySection | null> {
    const m = this.meetingInScope(ctx.recordId);
    if (!m) return null;
    const presentations = (m.presentations ?? [])
      .map(p => ('name' in p ? p.name : null) ?? ('title' in p ? (p as any).title : null))
      .filter(Boolean) as string[];
    const resources = (m.activityResources ?? []).map(r => r.indskr_ckmtitle).filter(Boolean) as string[];
    const content = [...presentations, ...resources];
    return content.length ? { title: 'Content Presented', data: { content } } : null;
  }

  @AIDataSource('meeting_objectives', { label: 'Meeting Objectives' })
  async meetingObjectives(ctx: DataSourceContext): Promise<SummarySection | null> {
    const m = this.meetingInScope(ctx.recordId);
    if (!m) return null;
    const objectives = (m.activityObjectives ?? []).map(o => o.indskr_name).filter(Boolean);
    return objectives.length ? { title: 'Meeting Objectives', data: { objectives } } : null;
  }

  @AIDataSource('meeting_attendees', { label: 'Attendees' })
  async meetingAttendees(ctx: DataSourceContext): Promise<SummarySection | null> {
    const m = this.meetingInScope(ctx.recordId);
    if (!m) return null;
    const hcps = (m.contacts ?? []).map(c => ({ name: c.fullName, specialty: c.primarySpecialtyName || c.combinedSpecialty || null }));
    const accounts = (m.accounts ?? []).map(a => a.accountName).filter(Boolean);
    if (!hcps.length && !accounts.length) return null;
    return {
      title: 'Attendees',
      data: { ...(hcps.length ? { hcps } : {}), ...(accounts.length ? { accounts } : {}) },
    };
  }

  @AIDataSource('meeting_indications', { label: 'Indications & Disease Areas' })
  async meetingIndications(ctx: DataSourceContext): Promise<SummarySection | null> {
    const m = this.meetingInScope(ctx.recordId);
    if (!m) return null;
    const indications = (m.activityProductIndications ?? []).map(i => ({
      name: i.indskr_name,
      keyMessages: (i.activityProductIndicationKeyMessages ?? []).map(k => k.indskr_name).filter(Boolean),
    }));
    const diseaseAreas = (m.activityDiseaseAreas ?? []).map(d => d.indskr_name).filter(Boolean);
    if (!indications.length && !diseaseAreas.length) return null;
    return {
      title: 'Indications & Disease Areas',
      data: { ...(indications.length ? { indications } : {}), ...(diseaseAreas.length ? { diseaseAreas } : {}) },
    };
  }

  @AIDataSource('meeting_key_message_sentiments', { label: 'Key Message Sentiments' })
  async meetingKeyMessageSentiments(ctx: DataSourceContext): Promise<SummarySection | null> {
    const m = this.meetingInScope(ctx.recordId);
    if (!m) return null;
    const sentiments: { hcp: string | null; messages: { keyMessage: string; sentiment: string }[] }[] = [];
    for (const cs of m.activityContactSentiments ?? []) {
      const messages = (cs.activityKeyMessageSentiments ?? [])
        .map(k => ({ keyMessage: k.indskr_name, sentiment: k.indskr_keymessagesentiment }))
        .filter(x => x.keyMessage || x.sentiment);
      if (messages.length) {
        const hcp = m.contacts?.find(c => c.ID === cs.indskr_contactid)?.fullName ?? null;
        sentiments.push({ hcp, messages });
      }
    }
    return sentiments.length ? { title: 'Key Message Sentiments', data: { sentiments } } : null;
  }

  // ---------------------------------------------------------------------------
  // Spotlight sources
  // ---------------------------------------------------------------------------

  @AIDataSource('today_meetings', { label: "Today's Meetings" })
  async spotlightTodayMeetings(_ctx: DataSourceContext): Promise<SummarySection | null> {
    const appointments = this.activityService.activities.filter(
      (a): a is AppointmentActivity => a instanceof AppointmentActivity,
    );

    // Today's meetings
    const todayMeetings = appointments.filter(a => isToday(new Date(a.scheduledStart)) && a.statusString !== 'Canceled');
    if (todayMeetings.length === 0) return null;

    const meetings = todayMeetings.map(m => ({
      time: m.scheduledStart ? new Date(m.scheduledStart).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null,
      subject: m.subject || 'Meeting',
      contacts: m.contacts?.map(c => c.fullName).filter(Boolean) ?? [],
      products: meetingProducts(m),
    }));
    return { title: "Today's Meetings", data: { meetings } };
  }

  @AIDataSource('upcoming_meetings', { label: 'Upcoming Meetings' })
  async spotlightUpcomingMeetings(_ctx: DataSourceContext): Promise<SummarySection | null> {
    const appointments = this.activityService.activities.filter(
      (a): a is AppointmentActivity => a instanceof AppointmentActivity,
    );

    // Upcoming meetings (next 7 days, excluding today)
    const upcoming = appointments
      .filter(a => isFuture(new Date(a.scheduledStart)) && !isToday(new Date(a.scheduledStart)) && a.statusString !== 'Canceled')
      .sort((a, b) => +new Date(a.scheduledStart) - +new Date(b.scheduledStart))
      .slice(0, 5);
    if (upcoming.length === 0) return null;

    const meetings = upcoming.map(m => ({
      date: new Date(m.scheduledStart).toLocaleDateString(),
      subject: m.subject || 'Meeting',
      contacts: m.contacts?.map(c => c.fullName).filter(Boolean) ?? [],
    }));
    return { title: 'Upcoming Meetings', data: { meetings } };
  }

  @AIDataSource('recent_meetings', { label: 'Recent Meetings' })
  async spotlightRecentMeetings(_ctx: DataSourceContext): Promise<SummarySection | null> {
    const appointments = this.activityService.activities.filter(
      (a): a is AppointmentActivity => a instanceof AppointmentActivity,
    );

    // Recent completed meetings — with full product/key message/presentation detail
    const recent = appointments
      .filter(a => isPast(new Date(a.scheduledStart)) && !isToday(new Date(a.scheduledStart)) && a.statusString !== 'Canceled')
      .sort((a, b) => +new Date(b.scheduledStart) - +new Date(a.scheduledStart))
      .slice(0, 5);
    if (recent.length === 0) return null;

    const meetings = recent.map(m => ({
      ...meetingToData(m),
      contacts: m.contacts?.map(c => c.fullName).filter(Boolean) ?? [],
    }));
    return { title: 'Recent Meetings', data: { meetings } };
  }

  @AIDataSource('portfolio', { label: 'Portfolio Summary' })
  async spotlightPortfolio(_ctx: DataSourceContext): Promise<SummarySection | null> {
    return {
      title: 'Portfolio Summary',
      data: {
        totalContacts: this.contactService.contacts.length,
        totalAccounts: this.accountService.accounts.length,
      },
    };
  }

  @AIDataSource('territory_rx', { label: 'Territory Rx & Sales Trends' })
  async spotlightTerritoryRx(_ctx: DataSourceContext): Promise<SummarySection | null> {
    const allRxData = await firstValueFrom(this.rxSalesDataService.rxSalesData$).catch(() => null);
    if (!allRxData || allRxData.length === 0) return null;

    // --- Contact-level Rx (prescriptions) ---
    const rxRows = allRxData.filter(d => d.type === 'rx' && d.period === Period.CM);

    // Group by product: total TRx, contacts writing, contacts with 0, biggest decliners
    // `change` is a percentage vs the prior period (the tile shows it as e.g. "900%"), so decliners
    // carry changePercent, not a raw count.
    const byProduct = new Map<string, { total: number; writers: number; zeroes: string[]; topDecliners: { name: string; changePercent: number }[] }>();
    for (const row of rxRows) {
      if (!('TRx' in row) || !row.TRx) continue;
      const name = row.product.name ?? row.product.ID;
      if (!byProduct.has(name)) byProduct.set(name, { total: 0, writers: 0, zeroes: [], topDecliners: [] });
      const entry = byProduct.get(name)!;
      entry.total += row.TRx.value;
      if (row.TRx.value > 0) {
        entry.writers++;
      } else {
        entry.zeroes.push(getContactNameFromRow(row));
      }
      if (row.TRx.change < -5) {
        entry.topDecliners.push({ name: getContactNameFromRow(row), changePercent: row.TRx.change });
      }
    }

    const products = [...byProduct.entries()].map(([product, stats]) => {
      stats.topDecliners.sort((a, b) => a.changePercent - b.changePercent);
      return {
        product,
        totalTRx: stats.total,
        prescribers: stats.writers,
        zeroScript: stats.zeroes.slice(0, 5),
        zeroScriptCount: stats.zeroes.length,
        topDecliners: stats.topDecliners.slice(0, 2),
      };
    });
    if (products.length === 0) return null;
    return { title: 'Territory Rx & Sales Trends (Current Month)', data: { products } };
  }

  @AIDataSource('direct_sales', { label: 'Account Direct Sales' })
  async spotlightDirectSales(_ctx: DataSourceContext): Promise<SummarySection | null> {
    const allRxData = await firstValueFrom(this.rxSalesDataService.rxSalesData$).catch(() => null);
    if (!allRxData || allRxData.length === 0) return null;

    // --- Account direct sales ---
    // Sales value (money) and sales volume (units) are distinct metrics — emit both as their own
    // attributes with their own percentage change, so the summary never conflates the two.
    const directRows = allRxData.filter(d => d.type === 'direct' && d.period === Period.CM);
    const directValue = (r: (typeof directRows)[number]): number =>
      'salesValue' in r && r.salesValue ? r.salesValue.value : 0;
    const sorted = [...directRows]
      .filter(d => 'salesValue' in d && d.salesValue)
      .sort((a, b) => directValue(b) - directValue(a))
      .slice(0, 5);

    const sales = sorted.map(row => {
      const account = ('account' in row) ? (row.account as { accountName?: string; displayName?: string }).accountName ?? (row.account as { displayName?: string }).displayName ?? 'Unknown' : 'Unknown';
      const sv = ('salesValue' in row && row.salesValue) ? row.salesValue : null;
      const svol = ('salesVolume' in row && row.salesVolume) ? row.salesVolume : null;
      return {
        account,
        product: row.product.name,
        salesValue: sv ? { amount: sv.value, changePercent: sv.change } : null,
        salesVolume: svol ? { units: svol.value, changePercent: svol.change } : null,
      };
    });
    if (sales.length === 0) return null;
    return { title: 'Account Direct Sales (Current Month)', data: { sales } };
  }

  @AIDataSource('brick_sales', { label: 'Territory (Brick) Sales' })
  async spotlightBrickSales(_ctx: DataSourceContext): Promise<SummarySection | null> {
    const allRxData = await firstValueFrom(this.rxSalesDataService.rxSalesData$).catch(() => null);
    if (!allRxData || allRxData.length === 0) return null;

    // --- Brick / territory ---
    const brickRows = allRxData.filter(d => d.type === 'brick' && d.period === Period.CM);
    // Emit the top bricks by sales magnitude (value, else volume) — mirrors direct_sales; the raw
    // rows arrive in offline-store order, so an unsorted slice would ground on arbitrary bricks.
    const brickMagnitude = (r: (typeof brickRows)[number]): number =>
      'salesValue' in r && r.salesValue ? r.salesValue.value
        : 'salesVolume' in r && r.salesVolume ? r.salesVolume.value : 0;
    // Sales value (money) and sales volume (units) are distinct metrics — emit both separately,
    // each with its own percentage change, so the summary never reports one as the other.
    const bricks = [...brickRows].sort((a, b) => brickMagnitude(b) - brickMagnitude(a)).slice(0, 5)
      .map(row => {
        const sv = ('salesValue' in row && row.salesValue) ? row.salesValue : null;
        const svol = ('salesVolume' in row && row.salesVolume) ? row.salesVolume : null;
        return {
          brick: ('brick' in row) ? row.brick.brickName : 'Unknown',
          product: row.product.name,
          salesValue: sv ? { amount: sv.value, changePercent: sv.change } : null,
          salesVolume: svol ? { units: svol.value, changePercent: svol.change } : null,
        };
      })
      .filter(b => b.salesValue || b.salesVolume);
    if (bricks.length === 0) return null;
    return { title: 'Territory (Brick) Sales (Current Month)', data: { bricks } };
  }

  @AIDataSource('call_plan_progress', { label: 'Call Plan Progress', featureAction: FeatureActionsMap.CALL_PLANS })
  async spotlightCallPlanProgress(_ctx: DataSourceContext): Promise<SummarySection | null> {
    const plans = await this.loadContactPlans();
    if (!plans || plans.length === 0) return null;

    // Mirror the spotlight call-plan tile so the summary quotes the same numbers the rep sees:
    // goal completion is total completed goals over total defined goals across the territory,
    // reach is contacts with any progress, and frequency caps actuals at their per-channel goals.
    let completedGoals = 0, definedGoals = 0, reach = 0, interactions = 0, customersWithGoals = 0;
    for (const p of plans) {
      completedGoals += p.completedGoals || 0;
      definedGoals += p.definedGoals || 0;
      if (p.percentage && p.percentage > 0) reach++;
      const capped =
        Math.min(p.actualF2FMeetings || 0, p.totalGoalF2FMeetings || 0) +
        Math.min(p.actualRemoteMeetings || 0, p.totalGoalRemoteMeetings || 0) +
        Math.min(p.actualEvents || 0, p.totalGoalEvents || 0);
      if (capped > 0) interactions += capped;
      if ((p.totalGoalF2FMeetings || 0) + (p.totalGoalRemoteMeetings || 0) + (p.totalGoalEvents || 0) > 0) customersWithGoals++;
    }
    const total = plans.length;
    const goalCompletionPct = definedGoals > 0 ? Math.round((completedGoals / definedGoals) * 100) : 0;
    const avgFrequency = customersWithGoals > 0 ? Math.round((interactions / customersWithGoals) * 10) / 10 : 0;

    const behind = plans
      .filter(p => (p.percentage ?? 0) < 50)
      .sort((a, b) => (a.percentage ?? 0) - (b.percentage ?? 0))
      .slice(0, 5)
      .map(p => ({ contact: p.customerName, completionPct: p.percentage ?? 0, endDate: fmtDate(p.indskr_enddate) }));

    return {
      title: 'Call Plan Progress',
      data: {
        contactCount: total,
        goalCompletion: `${completedGoals}/${definedGoals}`,
        goalCompletionPct,
        reach: `${reach}/${total}`,
        avgInteractionFrequency: avgFrequency,
        behind,
      },
    };
  }

  @AIDataSource('suggestions', { label: 'Action Suggestions' })
  async spotlightSuggestions(_ctx: DataSourceContext): Promise<SummarySection | null> {
    const suggestions = await this.suggestionsService.loadSuggestions().catch(() => []);
    if (suggestions.length === 0) return null;

    const items = suggestions.slice(0, 10).map(s => ({
      type: s.type,
      name: s.name,
      description: s.description,
      daysLeft: s.daysLeft !== undefined ? s.daysLeft : null,
    }));
    return { title: 'Action Suggestions', data: { suggestions: items } };
  }
}
